import {
  LogoutOutlined,
  LockOutlined,
  SettingOutlined,
  UserOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Avatar, Button, Form, Input, Modal, Space, Spin, Upload, message } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { changeSelfPassword, uploadAvatar } from '@/services/geo/api';
import { assetUrl } from '@/utils/assetUrl';
import { clearAuthSession, persistUserInfo, readStoredUserInfo } from '@/utils/authSession';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

const API_ORIGIN = 'https://api.sc9.cn';

const withApiOrigin = (url?: string) => {
  const val = String(url || '').trim();
  if (!val) return '';
  if (/^https?:\/\//i.test(val)) return val;
  return `${API_ORIGIN}${val.startsWith('/') ? '' : '/'}${val}`;
};

/** 与顶栏一致：有头像拼 API 域名，无则用默认图 */
const resolveUserAvatarSrc = (url?: string) => {
  const val = String(url || '').trim();
  if (!val) return assetUrl('icons/default_ico.png');
  return withApiOrigin(val);
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  const displayName = (currentUser as any)?.company_name || (currentUser as any)?.name || (currentUser as any)?.username || '用户';
  return <span className="anticon">{displayName}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');
  const [pwdForm] = Form.useForm();
  const [infoOpen, setInfoOpen] = React.useState(false);
  const [pwdSubmitting, setPwdSubmitting] = React.useState(false);
  const [avatarUploading, setAvatarUploading] = React.useState(false);
  const [avatarPreview, setAvatarPreview] = React.useState('');

  const handleUploadAvatar = async (file: File) => {
    setAvatarUploading(true);
    try {
      const res = await uploadAvatar(file);
      if (res?.code === 1) {
        const url = (res.data as any)?.url || res.data;
        const fullUrl = withApiOrigin(String(url || ''));
        setAvatarPreview(resolveUserAvatarSrc(fullUrl));
        const stored = readStoredUserInfo<Record<string, unknown>>() || {};
        const updated = { ...stored, ...(initialState?.currentUser as any), avatar: fullUrl };
        persistUserInfo(updated);
        flushSync(() => {
          setInitialState((s) => ({ ...s, currentUser: updated as any }));
        });
        message.success('头像更新成功');
        return false;
      }
      message.error(res?.msg || '头像上传失败');
    } finally {
      setAvatarUploading(false);
    }
    return false;
  };

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'changeInfo') {
      pwdForm.resetFields();
      const storedAvatar = readStoredUserInfo<{ avatar?: string }>()?.avatar;
      setAvatarPreview(
        resolveUserAvatarSrc((initialState?.currentUser as any)?.avatar || storedAvatar),
      );
      setInfoOpen(true);
      return;
    }
    if (key === 'logout') {
      Modal.confirm({
        title: '确认退出',
        content: '确定要退出登录吗？',
        okText: '确定',
        cancelText: '取消',
        onOk: () => {
          clearAuthSession();
          flushSync(() => {
            setInitialState((s) => ({ ...s, currentUser: undefined }));
          });
          history.push('/user/login');
        },
      });
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;
  const displayName = (currentUser as any)?.company_name || (currentUser as any)?.name || (currentUser as any)?.username;

  if (!currentUser || !displayName) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: 'center',
            icon: <UserOutlined />,
            label: '个人中心',
          },
          {
            key: 'settings',
            icon: <SettingOutlined />,
            label: '个人设置',
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'changeInfo',
      icon: <LockOutlined />,
      label: '修改信息',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>

      <Modal
        title="修改信息"
        open={infoOpen}
        okText="确认修改密码"
        cancelText="关闭"
        confirmLoading={pwdSubmitting}
        onCancel={() => setInfoOpen(false)}
        onOk={async () => {
          try {
            const values = await pwdForm.validateFields();
            setPwdSubmitting(true);
            const res = await changeSelfPassword({
              old_password: values.old_password,
              new_password: values.new_password,
            });
            if (res?.code === 1) {
              message.success('密码修改成功');
              setInfoOpen(false);
              pwdForm.resetFields();
              return;
            }
            message.error(res?.msg || '密码修改失败');
          } finally {
            setPwdSubmitting(false);
          }
        }}
        destroyOnClose
      >
        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <div style={{ marginBottom: 12, fontWeight: 500 }}>头像</div>
          <Space direction="vertical" size={12} align="center">
            <Avatar
              size={72}
              src={avatarPreview || assetUrl('icons/default_ico.png')}
              icon={<UserOutlined />}
            />
            <Upload
              showUploadList={false}
              accept="image/jpeg,image/png,image/gif,image/webp"
              beforeUpload={(file) => {
                void handleUploadAvatar(file);
                return false;
              }}
            >
              <Button icon={<UploadOutlined />} loading={avatarUploading}>
                更新头像
              </Button>
            </Upload>
          </Space>
        </div>

        <Form form={pwdForm} layout="vertical">
          <Form.Item
            name="old_password"
            label="旧密码"
            rules={[{ required: true, message: '请输入旧密码' }]}
          >
            <Input.Password placeholder="请输入旧密码" />
          </Form.Item>
          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '新密码至少6位' },
            ]}
          >
            <Input.Password placeholder="请输入新密码（至少6位）" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认新密码"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的新密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
